import { useEffect, useState } from 'react';

/**
 * Reactive media-query hook tied to the project's 600px mobile cutoff
 * (same breakpoint the Topbar's hamburger uses to swap between the
 * desktop SectionNav and the mobile drawer).
 *
 * Returns `true` when the viewport is narrower than 600px.
 *
 * Used by MapExplorer to switch between the desktop full-viewport
 * map shell (absolute sidebar + legend overlays) and a stacked
 * mobile layout where filters, map, and metros list flow vertically.
 * A CSS-only `hidden`/`block` toggle would mount two `LeafletMap`s
 * — which fails because Leaflet can't initialize tile sizes inside
 * a `display: none` parent — so we route through this hook and
 * render exactly one layout at a time.
 *
 * SSR-safe: returns `false` during server render; hydration triggers
 * a single re-render once `matchMedia` is available.
 */
const MOBILE_QUERY = '(max-width: 599px)';

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return isMobile;
}
